import React, { useEffect, useState } from 'react';
import { Eye, Edit3, Send, Download, Search, Filter, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import StatusBadge from './StatusBadge';
import { Invoice, BackendInvoiceStatus } from '../api/billingApi';
import Spinner from './Spinner';
import { formatCurrencyCode } from '../utils/currency';

const ITEMS_PER_PAGE = 10;

interface InvoiceTableProps {
  invoices: Invoice[];
  loading: boolean;
  filterStatus?: BackendInvoiceStatus | 'All';
  onEdit?: (invoice: Invoice) => void;
  onDelete?: (id: string) => void;
  onSend?: (id: string) => void;
  onDownload?: (id: string, name: string) => void;
  onView?: (id: string) => void;
}

export default function InvoiceTable({
  invoices,
  loading,
  filterStatus = 'All',
  onEdit,
  onDelete,
  onSend,
  onDownload,
  onView,
}: InvoiceTableProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const [dueFrom, setDueFrom] = useState("");
  const [dueTo, setDueTo] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");

  useEffect(() => {
  setCurrentPage(1);
}, [
  filterStatus,
  searchTerm,
  dueFrom,
  dueTo,
  minAmount,
  maxAmount,
]);

const normalizedSearch = searchTerm.trim().toLowerCase();

  const filtered = invoices.filter((inv) => {
    // Status filter from the parent tabs:
    // All / Drafts / Sent / Paid / Overdue
    const matchesStatus =
      filterStatus === "All" ||
      inv.status === filterStatus;

    // Search by client name or invoice number
    const customerName =
      inv.customerName?.toLowerCase() ?? "";

    const invoiceNumber =
      inv.invoiceNumber?.toLowerCase() ?? "";

    const matchesSearch =
      !normalizedSearch ||
      customerName.includes(normalizedSearch) ||
      invoiceNumber.includes(normalizedSearch);

    // Due date filtering
    const invoiceDueDate = inv.dueDate
      ? new Date(inv.dueDate)
      : null;

    const dueFromDate = dueFrom
      ? new Date(`${dueFrom}T00:00:00`)
      : null;

    const dueToDate = dueTo
      ? new Date(`${dueTo}T23:59:59.999`)
      : null;

    const hasValidDueDate =
      invoiceDueDate !== null &&
      !Number.isNaN(invoiceDueDate.getTime());

    const matchesDueFrom =
      !dueFromDate ||
      (hasValidDueDate &&
        invoiceDueDate! >= dueFromDate);

    const matchesDueTo =
      !dueToDate ||
      (hasValidDueDate &&
        invoiceDueDate! <= dueToDate);

    // Amount filtering
    const parsedMinAmount =
      minAmount.trim() === ""
        ? null
        : Number(minAmount);

    const parsedMaxAmount =
      maxAmount.trim() === ""
        ? null
        : Number(maxAmount);

    const matchesMinAmount =
      parsedMinAmount === null ||
      Number.isNaN(parsedMinAmount) ||
      inv.totalAmount >= parsedMinAmount;

    const matchesMaxAmount =
      parsedMaxAmount === null ||
      Number.isNaN(parsedMaxAmount) ||
      inv.totalAmount <= parsedMaxAmount;

    return (
      matchesStatus &&
      matchesSearch &&
      matchesDueFrom &&
      matchesDueTo &&
      matchesMinAmount &&
      matchesMaxAmount
    );
  });

  const totalPages = Math.max(
    1,
    Math.ceil(filtered.length / ITEMS_PER_PAGE)
  );

  const safeCurrentPage = Math.min(
    currentPage,
    totalPages
  );

  const startIndex =
    (safeCurrentPage - 1) * ITEMS_PER_PAGE;

  const paginatedData = filtered.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE
  );

  const activeAdvancedFilterCount = [
    dueFrom,
    dueTo,
    minAmount,
    maxAmount,
  ].filter((value) => value !== "").length;

  const hasAdvancedFilters =
    activeAdvancedFilterCount > 0;

  const clearAdvancedFilters = () => {
    setDueFrom("");
    setDueTo("");
    setMinAmount("");
    setMaxAmount("");
    setCurrentPage(1);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row gap-4 justify-between items-stretch md:items-center bg-white p-4 rounded-2xl border border-slate-100 shadow-sm">
        <div className="relative w-full sm:w-96">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input 
            type="text" 
            placeholder="Search client or invoice #..." 
            className="input-base pl-10 h-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button
          type="button"
          onClick={() =>
            setShowFilters((current) => !current)
          }
          className={`btn-secondary flex h-10 w-full items-center justify-center gap-2 whitespace-nowrap px-4 sm:w-auto ${
            showFilters || hasAdvancedFilters
              ? "border-strivre-burgundy text-white"
              : ""
          }`}
        >
          <Filter size={18} />

          <span>Filters</span>

          {hasAdvancedFilters && (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-strivre-burgundy px-1.5 text-[10px] font-bold text-white">
              {activeAdvancedFilterCount}
            </span>
          )}
        </button>
      </div>
      {showFilters && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="font-semibold text-slate-900">
                Advanced Filters
              </h3>

              <p className="mt-1 text-xs text-slate-500">
                Narrow invoices by due date or total amount.
              </p>
            </div>

            {hasAdvancedFilters && (
              <button
                type="button"
                onClick={clearAdvancedFilters}
                className="self-start text-sm font-semibold text-strivre-burgundy hover:underline sm:self-auto"
              >
                Clear Filters
              </button>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {/* Due From */}
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">
                Due From
              </label>

              <input
                type="date"
                className="input-base"
                value={dueFrom}
                max={dueTo || undefined}
                onChange={(e) =>
                  setDueFrom(e.target.value)
                }
              />
            </div>

            {/* Due To */}
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">
                Due To
              </label>

              <input
                type="date"
                className="input-base"
                value={dueTo}
                min={dueFrom || undefined}
                onChange={(e) =>
                  setDueTo(e.target.value)
                }
              />
            </div>

            {/* Minimum Amount */}
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">
                Minimum Amount
              </label>

              <input
                type="number"
                min="0"
                step="0.01"
                className="input-base"
                placeholder="0.00"
                value={minAmount}
                onChange={(e) =>
                  setMinAmount(e.target.value)
                }
              />
            </div>

            {/* Maximum Amount */}
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">
                Maximum Amount
              </label>

              <input
                type="number"
                min="0"
                step="0.01"
                className="input-base"
                placeholder="0.00"
                value={maxAmount}
                onChange={(e) =>
                  setMaxAmount(e.target.value)
                }
              />
            </div>
          </div>

          {minAmount &&
            maxAmount &&
            Number(minAmount) > Number(maxAmount) && (
              <p className="mt-3 text-xs font-medium text-red-600">
                Minimum amount cannot be greater than
                maximum amount.
              </p>
            )}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-strivre-lightnavy overflow-hidden shadow-sm">
        {loading && (
          <div className="p-8 flex justify-center items-center">
            <Spinner size="h-8 w-8" />
            <p className="ml-3 text-slate-500">Loading invoices...</p>
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="p-12 text-center text-slate-400 italic">No invoices found.</div>
        )}

        {/* Mobile View: Card List */}
        <div className="md:hidden divide-y divide-slate-100">
          {paginatedData.map((inv) => (
            <div key={inv.invoiceNumber} className="p-5 space-y-4 active:bg-slate-50 transition-colors">
              <div className="flex flex-row justify-between items-start gap-2">
                <div className="text-left">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{inv.invoiceNumber}</span>
                  <h4 className="text-sm sm:text-base font-bold text-slate-900 mt-0.5 break-words max-w-[150px] sm:max-w-none">{inv.customerName}</h4>
                </div>
                <StatusBadge status={inv.status} />
              </div>
              <div className="flex flex-row justify-between items-end gap-2">
                <div className="text-left space-y-0.5">
                  <p className="text-[11px] text-slate-500 font-semibold">Due {new Date(inv.dueDate).toLocaleDateString()}</p>
                  <p className="text-xl font-black text-slate-900 tracking-tight">
                    {formatCurrencyCode(
                      inv.totalAmount,
                      inv.currencyCode ?? 'SGD'
                    )}
                  </p>
                </div>
                <div className="flex gap-2">
                      <button onClick={() => onView?.(inv.id)} className="p-2.5 bg-strivre-softgrey text-strivre-burgundy rounded-xl active:scale-95 transition-all"><Eye size={18} /></button>
                  <button onClick={() => onEdit?.(inv)} className="p-2.5 bg-strivre-softgrey text-slate-600 rounded-xl active:scale-95 transition-all"><Edit3 size={18} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop View: Table */}
        <div className="hidden md:block overflow-x-auto" style={{ minHeight: loading || paginatedData.length === 0 ? '0px' : 'auto' }}>
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-strivre-lightnavy">
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Invoice #</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Client</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Due Date</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {paginatedData.map((inv) => (
                <tr key={inv.invoiceNumber} className="hover:bg-slate-50/50 transition-colors group">
                  <td className="px-6 py-4 font-medium text-slate-900">{inv.invoiceNumber}</td>
                  <td className="px-6 py-4 text-slate-600">{inv.customerName}</td>
                  <td className="px-6 py-4 font-bold text-slate-900">
                    {formatCurrencyCode(
                      inv.totalAmount,
                      inv.currencyCode ?? 'SGD'
                    )}
                  </td>
                  <td className="px-6 py-4"><StatusBadge status={inv.status} /></td>
                  <td className="px-6 py-4 text-slate-500">{new Date(inv.dueDate).toLocaleDateString()}</td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end gap-2 transition-opacity">
                      <button onClick={() => onView?.(inv.id)} className="p-2 bg-slate-50 text-slate-500 rounded-lg border border-slate-100 shadow-sm hover:bg-white hover:text-strivre-burgundy hover:border-strivre-lightnavy" title="View"><Eye size={16} /></button>
                      <button onClick={() => onEdit?.(inv)} className="p-2 bg-slate-50 text-slate-500 rounded-lg border border-slate-100 shadow-sm hover:bg-white hover:text-strivre-burgundy hover:border-strivre-lightnavy" title="Edit"><Edit3 size={16} /></button>
                      {inv.status === BackendInvoiceStatus.Draft && (
                        <button onClick={() => onSend?.(inv.id)} className="p-2 bg-slate-50 text-slate-500 rounded-lg border border-slate-100 shadow-sm hover:bg-white hover:text-emerald-600 hover:border-strivre-lightnavy" title="Send"><Send size={16} /></button>
                      )}
                      <button onClick={() => onDownload?.(inv.id, inv.invoiceNumber)} className="p-2 bg-slate-50 text-slate-500 rounded-lg border border-slate-100 shadow-sm hover:bg-white hover:text-slate-900 hover:border-strivre-lightnavy" title="Download PDF"><Download size={16} /></button>
                      <button 
                        onClick={() => onDelete?.(inv.id)} 
                        className="p-2 bg-slate-50 text-slate-500 rounded-lg border border-slate-100 shadow-sm hover:bg-white hover:text-red-600 hover:border-strivre-lightnavy" 
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {filtered.length > 0 && (
          <div className="px-6 py-4 bg-white border-t border-strivre-lightnavy flex flex-col sm:flex-row items-center justify-between gap-4">
            <span className="text-xs md:text-sm text-slate-500 font-medium">
              Showing {paginatedData.length === 0 ? 0 : ((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, filtered.length)} of {filtered.length} invoices
            </span>
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 border border-strivre-lightnavy rounded-lg bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <ChevronLeft size={18} />
              </button>
              <span className="text-sm font-medium text-slate-700 min-w-[3rem] text-center">
                Page {currentPage} of {totalPages}
              </span>
              <button 
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 border border-strivre-lightnavy rounded-lg bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}